
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useTranslation } from "react-i18next";
import BasicInfoForm from "./profile/BasicInfoForm";
import ContactInfoForm from "./profile/ContactInfoForm";
import ServiceCategorySelector from "./profile/ServiceCategorySelector";
import AvatarUploadSection from "./profile/AvatarUploadSection";
import PhotoGalleryManager from "./profile/PhotoGalleryManager";
import InsuranceInfoForm from "./profile/InsuranceInfoForm";
import LanguagesSpokenSelector from "./profile/LanguagesSpokenSelector";
import IssueTagSelector from "./profile/IssueTagSelector";

interface ProfileManagerProps {
  userId: string;
}

export default function ProfileManager({ userId }: ProfileManagerProps) {
  const [profile, setProfile] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { toast } = useToast();
  const { t } = useTranslation('dashboard');

  const fetchProfile = async () => {
    try {
      setIsLoading(true);
      const { data, error } = await supabase
        .from("practitioners")
        .select("*")
        .eq("id", userId)
        .single();

      if (error) throw error;
      setProfile(data);
    } catch (error) {
      console.error("Error fetching profile:", error);
      toast({
        title: "Error",
        description: "Failed to load profile data.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchProfile();
  }, [userId]);

  if (isLoading) {
    return <div>{t('loading')}</div>;
  }

  if (!profile) {
    return <div>Profile not found</div>;
  }

  return (
    <div className="space-y-6">
      <AvatarUploadSection
        userId={userId}
        profile={profile}
        onUpdate={fetchProfile}
      />
      
      <BasicInfoForm
        practitionerId={userId}
        initialData={profile}
        onUpdate={fetchProfile}
      />
      
      <LanguagesSpokenSelector
        practitionerId={userId}
        initialLanguages={profile.languages_spoken || []}
        onUpdate={fetchProfile}
      />
      
      <IssueTagSelector
        practitionerId={userId}
        onUpdate={fetchProfile}
      />
      
      <ServiceCategorySelector
        practitionerId={userId}
        onUpdate={fetchProfile}
      />
      
      <InsuranceInfoForm
        practitionerId={userId}
        onUpdate={fetchProfile}
      />
      
      <ContactInfoForm
        practitionerId={userId}
        initialData={profile}
        onUpdate={fetchProfile}
      />
      
      <PhotoGalleryManager
        practitionerId={userId}
        onUpdate={fetchProfile}
      />
    </div>
  );
}
